import { useNavigation } from '@react-navigation/core';
import { BaseText, Button, Headline2, theme } from 'aimo-ui';
import { ButtonTypes } from 'aimo-ui/lib/typescript/src/components/Button/types';
import Icon from 'components/icons/Icon';
import SvgImage, { SvgName } from 'components/svgImage/SvgImage';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, Pressable, StyleSheet } from 'react-native';
import { Shadow } from 'react-native-shadow-2';
import styled from 'styled-components/native';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import { RootNavProps } from 'types/navigation';
import { Tutorial } from 'types/states';
import { isWeb } from 'utils/deviceUtils';
import { completeTutorial } from 'utils/tutorialUtils';

export type CarouselTutorialInfo = {
  image?: SvgName;
  translationKey: string;
  imageStyle?: any;
  buttonPress?: 'PAYMENT_METHODS' | 'COMPLETE';
  buttonType?: ButtonTypes;
  imageShadow?: boolean;
  tutorialId?: Tutorial;
};

const Container = styled.View`
  flex: 1;
  padding: 0px ${isWeb ? 40 : 10}px;
`;

const ImageContainer = styled.View`
  padding-top: 10px;
  margin-bottom: 10px;
  flex: 1;
`;

const TextButtonContainer = styled.View`
  flex-shrink: 1;
`;

const styles = StyleSheet.create({
  shadow: {
    flex: 1,
    borderRadius: 50,
    borderBottomEndRadius: 5,
    borderBottomStartRadius: 5,
    marginBottom: 20,
  },
  shadowContainer: {
    flex: 1,
    marginTop: 10,
  },
});

const ImageContainerShadow = ({
  children,
  imageStyle,
  imageShadow,
}: {
  children: React.ReactNode;
  imageStyle: any;
  imageShadow?: boolean;
}) => {
  return (
    <Shadow
      disabled={!imageShadow}
      stretch
      distance={12}
      startColor={theme.colors.gray50}
      sides={{ end: true, bottom: false, top: true, start: true }}
      style={[styles.shadow]}
      containerStyle={[styles.shadowContainer]}>
      <ImageContainer style={imageStyle}>{children}</ImageContainer>
    </Shadow>
  );
};

const CustomLink = ({ link, text }: { link: string; text?: string }) => {
  const displayText = text ?? link.split('/').find(Boolean) ?? link;
  return (
    <Pressable
      style={({ pressed }) => [
        {
          marginBottom: -2,
          opacity: pressed ? 0.5 : 1,
          flexDirection: 'row',
          alignItems: 'flex-end',
          gap: 2,
        },
      ]}
      onPress={() => Linking.openURL(`https://${link}`)}>
      <BaseText
        style={{
          textDecorationLine: 'underline',
          textDecorationStyle: 'solid',
          textDecorationColor: theme.colors.primary,
        }}
        color={theme.colors.primary}>
        {displayText}
      </BaseText>
      <Icon color={theme.colors.transparent} size="xs" name="External-Link" />
    </Pressable>
  );
};

const ContentText = ({ text }: { text: string }) => {
  const paragraphs = text.split('\n').map((p) => p.trim());

  return (
    <BaseText>
      {paragraphs.map((paragraph, pIndex) => {
        // Check if paragraph is wrapped in curly braces
        const leftAlign = paragraph.startsWith('{') && paragraph.endsWith('}');
        // Remove braces if left align
        const cleanParagraph = leftAlign ? paragraph.slice(1, -1) : paragraph;

        const HTML_TAG = '<[^>]+>';
        const BOLD_TEXT = '\\*{2}[^*]+\\*{2}';

        const chunks = cleanParagraph.split(
          new RegExp(`(${HTML_TAG}|${BOLD_TEXT})`)
        );

        return (
          <BaseText center={!leftAlign} key={`paragraph-${pIndex}`}>
            {chunks.map((chunk, index) => {
              if (chunk.startsWith('<') && chunk.endsWith('>')) {
                const linkContent = chunk.slice(1, -1);
                const [altText, url] = linkContent.split('|');
                return (
                  <CustomLink
                    key={`tutorial-link-${chunk ?? index}`}
                    link={url ?? altText}
                    text={url ? altText : undefined}
                  />
                );
              }
              if (chunk.startsWith('**') && chunk.endsWith('**')) {
                const boldText = chunk.slice(2, -2);
                return (
                  <BaseText
                    key={`tutorial-bold-${index}`}
                    style={{ fontWeight: 'bold' }}>
                    {boldText}
                  </BaseText>
                );
              }
              return (
                <BaseText key={`tutorial-text-${index}`}>{chunk}</BaseText>
              );
            })}
            {pIndex < paragraphs.length - 1 && <BaseText>{'\n'}</BaseText>}
          </BaseText>
        );
      })}
    </BaseText>
  );
};

export const TutorialItem: React.FC<{ item: CarouselTutorialInfo }> = ({
  item: {
    image,
    imageStyle,
    buttonPress,
    translationKey: tKey,
    buttonType = 'primary',
    imageShadow,
    tutorialId,
  },
}) => {
  const { navigate } = useNavigation<RootNavProps>();
  const { t } = useTranslation();

  const navigatePress = () => {
    if (buttonPress === 'PAYMENT_METHODS') {
      navigate('mainTabs', {
        screen: 'accountScreen',
        params: { screen: 'myPaymentMethods' },
      });
    }
    completeTutorial('firstTimer');
  };

  const translationKey = tutorialId ? `${tutorialId}.${tKey}` : tKey;

  const isUrl = image?.includes('.svg');

  return (
    <Container>
      {image && (
        <ImageContainerShadow imageShadow={imageShadow} imageStyle={imageStyle}>
          <SvgImage
            height={isUrl && isWeb ? 250 : undefined}
            width={isUrl && isWeb ? 250 : undefined}
            name={image}
            align="center"
            uri={isUrl ? image : undefined}
            testID={`first-timer-${image}`}
          />
        </ImageContainerShadow>
      )}
      <TextButtonContainer>
        <ColumnWithMargins mb={20}>
          <ColumnWithMargins mb={10}>
            <Headline2 center>
              {t(`tutorial.${translationKey}.title`)}
            </Headline2>
          </ColumnWithMargins>
          <ContentText text={t(`tutorial.${translationKey}.info`)} />
        </ColumnWithMargins>
        {buttonPress && (
          <Button
            buttonType={buttonType}
            text={t(`tutorial.${translationKey}.button`)}
            size="l"
            onPress={navigatePress}
          />
        )}
      </TextButtonContainer>
    </Container>
  );
};
